if (!customElements.get("related-products-swiper")) {
  class RelatedProductsSwiper extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.swiper = null;
      this.observer = null;
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized || this.observer) return;

      this.slider = this.querySelector("[data-related-products-swiper]");

      if (!this.slider) return;

      const slides = this.querySelectorAll("[data-related-product-slide]");

      if (slides.length < 2) {
        this.dataset.overflow = "false";
        this.classList.add("is-static");
        return;
      }

      if ("IntersectionObserver" in window && !this.isThemeEditor()) {
        this.observe();
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      this.initialized = false;
      this.classList.remove("is-initialized");
    }

    observe() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.observer?.disconnect();
            this.observer = null;
            this.init();
          });
        },
        {
          rootMargin: "0px",
          threshold: 0.01,
        },
      );

      this.observer.observe(this);
    }

    async init() {
      if (this.initialized || !this.slider) return;

      try {
        await this.loadSwiper();

        if (!window.Swiper) {
          console.warn("[related-products-swiper] Swiper is not available.");
          return;
        }

        this.swiper = new window.Swiper(this.slider, this.getOptions());
        this.initialized = true;
        this.classList.add("is-initialized");
        this.updateOverflow();
        this.updateButtonPosition();
      } catch (error) {
        console.warn("[related-products-swiper] Failed to initialize slider:", error);
      }
    }

    getOptions() {
      const showNavigationMobile = this.dataset.showNavigationMobile === "true";
      const showNavigationDesktop = this.dataset.showNavigationDesktop === "true";
      const hasNavigation = showNavigationMobile || showNavigationDesktop;

      return {
        slidesPerView: this.getSlidesPerView("mobile"),
        spaceBetween: this.getGap("mobile"),
        watchOverflow: true,
        resizeObserver: true,
        observeParents: true,
        grabCursor: true,
        navigation: hasNavigation
          ? {
              nextEl: this.querySelector("[data-related-products-next]"),
              prevEl: this.querySelector("[data-related-products-previous]"),
              enabled: showNavigationMobile,
            }
          : false,
        breakpoints: {
          750: {
            slidesPerView: this.getSlidesPerView("desktop"),
            spaceBetween: this.getGap("desktop"),
            navigation: hasNavigation
              ? {
                  enabled: showNavigationDesktop,
                }
              : false,
          },
        },
        on: {
          afterInit: () => {
            this.updateOverflow();
            this.updateButtonPosition();
          },
          lock: () => this.updateOverflow(),
          unlock: () => this.updateOverflow(),
          resize: () => {
            this.updateOverflow();
            this.updateButtonPosition();
          },
          breakpoint: () => this.updateButtonPosition(),
          slidesLengthChange: () => this.updateOverflow(),
        },
      };
    }

    getSlidesPerView(device) {
      const mode = this.dataset[`${device}SlidesPerViewMode`];

      if (mode === "auto") return "auto";

      const value = this.dataset[`${device}SlidesPerView`];

      const parsedValue = parseFloat(value || "");

      if (Number.isNaN(parsedValue) || parsedValue <= 0) return device === "desktop" ? 4 : 1.2;

      return parsedValue;
    }

    getGap(device) {
      const gap = parseFloat(this.dataset[`${device}Gap`] || "");

      if (!Number.isNaN(gap)) return gap;

      const styles = getComputedStyle(this);

      return parseFloat(styles.getPropertyValue(`--rps-${device}-gap`)) || 0;
    }

    updateOverflow() {
      if (!this.swiper) {
        this.dataset.overflow = "false";
        return;
      }

      this.dataset.overflow = String(!this.swiper.isLocked);
    }

    updateButtonPosition() {
      requestAnimationFrame(() => {
        const media = this.querySelector(".related-products-swiper-card__media");

        if (!media) return;

        const mediaHeight = media.getBoundingClientRect().height;

        if (!mediaHeight) return;

        this.style.setProperty("--rps-card-media-center", `${mediaHeight / 2}px`);
      });
    }

    async loadSwiper() {
      if (window.Swiper) return;

      if (typeof window.loadSwiper === "function") {
        await window.loadSwiper();
        return;
      }

      if (window.__relatedProductsSwiperPromise) {
        return window.__relatedProductsSwiperPromise;
      }

      window.__relatedProductsSwiperPromise = Promise.all([
        this.loadStylesheet(this.dataset.swiperCss),
        this.loadScript(this.dataset.swiperJs),
      ]);

      return window.__relatedProductsSwiperPromise;
    }

    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (!src) {
          reject(new Error("Missing Swiper JS asset URL."));
          return;
        }

        const existingScript = document.querySelector(`script[src="${src}"]`);

        if (existingScript) {
          if (existingScript.dataset.loaded === "true") {
            resolve();
            return;
          }

          existingScript.addEventListener("load", () => resolve(), {once: true});
          existingScript.addEventListener("error", reject, {once: true});
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;

        script.addEventListener(
          "load",
          () => {
            script.dataset.loaded = "true";
            resolve();
          },
          {once: true},
        );

        script.addEventListener("error", reject, {once: true});

        document.head.appendChild(script);
      });
    }

    loadStylesheet(href) {
      return new Promise((resolve) => {
        if (!href) {
          resolve();
          return;
        }

        const existingLink = document.querySelector(`link[href="${href}"]`);

        if (existingLink) {
          resolve();
          return;
        }

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.onload = () => resolve();
        link.onerror = () => resolve();

        document.head.appendChild(link);
      });
    }

    isThemeEditor() {
      return window.Shopify && window.Shopify.designMode;
    }
  }

  customElements.define("related-products-swiper", RelatedProductsSwiper);
}
